/**
 * Renders report markdown as React elements.
 *
 * react-markdown builds elements directly, so no HTML string is produced and no
 * `dangerouslySetInnerHTML` is needed — the report content comes from an LLM, and this is
 * structurally safer than escaping by hand. `rehype-sanitize` runs here as well as in
 * `shared/lib/markdown`, so both surfaces are governed by one schema.
 */
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { cleanReportContent, remarkImagesToText } from "@/shared/lib/markdown";

// Must stay the same plugin set as the string pipeline in shared/lib/markdown.ts — that is
// what makes the screen and the clipboard agree. remarkImagesToText rewrites image nodes to
// plain alt text before react-markdown's internal remark-rehype step ever sees them — see its
// doc comment in shared/lib/markdown.ts for why an <img> must never reach the DOM here.
const REMARK_PLUGINS = [remarkGfm, remarkBreaks, remarkImagesToText];
const REHYPE_PLUGINS = [rehypeSanitize];

const COMPONENTS: Components = {
  // A report card is narrow and a Fact-Check table has five columns. Without this wrapper the
  // table distends the layout instead of scrolling inside it.
  // Destructure node away: react-markdown passes the hast AST node through ExtraProps, and
  // TypeScript excess-property checking does not catch spread props, so it would leak onto the DOM.
  table: ({ children, node, ...props }) => (
    <div data-qa="markdown-table" className="overflow-x-auto my-3">
      <table {...props}>{children}</table>
    </div>
  ),
};

export function Markdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={REHYPE_PLUGINS}
      components={COMPONENTS}
    >
      {cleanReportContent(content)}
    </ReactMarkdown>
  );
}
